import clsx from 'clsx'
import { useTranslation } from 'next-i18next'
import React, { FC, useEffect, useRef, useState } from 'react'
import { useIntersection } from 'react-use'

import Button from '@/components/Button'
import Heading from '@/components/Heading'
import Link from '@/components/Link'
import Picture from '@/components/Picture'

import DefaultComponentProps from '@/types/DefaultComponentProps'

import styles from './Section.module.scss'
import { routes } from '@/config'

type ItemsProps = {
  title: string
  summary: string
}

type SmmListProps = {
  title: string
}

type SmmProps = {
  heading: string
  from: string
  budget: string
  percent: string
  list1: SmmListProps[]
  list2: SmmListProps[]
}

type ServicesProps = {
  list: { [key: string]: ItemsProps }
  heading: string
  summary: string
  smm: SmmProps
}

const MAIN_LIST: { [kay: string]: { image: string } } = {
  audit: {
    image: 'pic5.png'
  },
  quick_start: {
    image: 'pic6.png'
  },
  flexible_pricing: {
    image: 'pic7.png'
  },
  working_directly: {
    image: 'pic8.png'
  }
}

export const Services: FC<DefaultComponentProps> = ({ className = '' }) => {
  const { t, i18n: { language = 'en' } = {} } = useTranslation()

  const { list, heading, summary, smm } = t('services:data', {
    returnObjects: true
  }) as ServicesProps

  const [animationStart, setAnimationStart] = useState(false)

  const intersectionRef = useRef(null)

  const intersection = useIntersection(intersectionRef, {
    root: null,
    rootMargin: '0px',
    threshold: 0.4
  })

  useEffect(() => {
    if (intersection?.isIntersecting) {
      setAnimationStart(true)
    }
  }, [intersection?.isIntersecting])

  return (
    <section
      ref={intersectionRef}
      className={clsx(styles['section'], className, {
        [styles['section_animation-start']]: animationStart
      })}
    >
      <div className={styles['section__content']}>
        <div className={styles['section__head']}>
          <Heading className={styles['section__heading']} level={2}>
            {heading}
          </Heading>
          <div
            className={styles['section__summary']}
            dangerouslySetInnerHTML={{ __html: summary }}
          />
        </div>
        <div className={styles['section__body']}>
          <div className={styles['section__col']}>
            <ul className={styles['section__list']}>
              {Object.entries(list).map(([key, { title, summary }]) => (
                <li key={title} className={styles['section__list-item']}>
                  <figure className={styles['section__list-fig']}>
                    <Picture
                      src={`/images/home/${language}/${MAIN_LIST[key].image}`}
                      alt={title}
                      className={styles['section__list-img']}
                    />
                  </figure>
                  <Heading
                    className={styles['section__list-heading']}
                    level={6}
                  >
                    {title}
                  </Heading>
                  <div className={styles['section__list-summary']}>
                    {summary}
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className={styles['section__col']}>
            <div className={styles['section__smm']}>
              <Heading className={styles['section__smm-heading']} level={4}>
                {smm.heading}
              </Heading>
              <div className={styles['section__smm-percent']}>
                <div className={styles['section__smm-percent-col']}>
                  <div className={styles['section__smm-percent-from']}>
                    {smm.from}
                  </div>
                  <div className={styles['section__smm-percent-budget']}>
                    {smm.budget}
                  </div>
                </div>
                <div className={styles['section__smm-percent-col']}>
                  <div className={styles['section__smm-percent-value']}>5%</div>
                </div>
              </div>
              <div className={styles['section__smm-lists']}>
                <div className={styles['section__smm-lists-col']}>
                  <ul className={styles['section__smm-lists-list']}>
                    {Object.entries(smm.list1).map(([key, { title }]) => (
                      <li
                        className={styles['section__smm-lists-item']}
                        key={key}
                      >
                        {title}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={styles['section__smm-lists-col']}>
                  <ul className={styles['section__smm-lists-list']}>
                    {Object.entries(smm.list2).map(([key, { title }]) => (
                      <li
                        className={styles['section__smm-lists-item']}
                        key={key}
                      >
                        {title}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={styles['section__foot']}>
          <Button
            title={t('forms:buttons.signup.label') as string}
            href={routes.REGISTER}
            as={Link}
            size={'l'}
            className={styles['section__button-link']}
          >
            {t('forms:buttons.learnMore.label')}
          </Button>
          <Button
            title={t('forms:buttons.signin.label') as string}
            href={routes.LOGIN}
            as={Link}
            className={styles['section__button-link']}
            size={'l'}
            variant={'ghost'}
          >
            {t('forms:buttons.fillOutTheBrief.label')}
          </Button>
        </div>
      </div>
    </section>
  )
}
